import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import clsx from 'clsx'
import Search from '@/modules/base/components/search/search'
import { isEmpty } from 'lodash'
import DatevClientSelectionItem from './datev-client-selection-item'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { ButtonSquare, CancelButton } from '@/modules/base/components/buttons'
import CloseIcon from '@mui/icons-material/Close'

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
  },
  search: {
    marginBottom: 20,
    padding: '0px 40px',
  },
  scrollArea: {
    maxHeight: '100%',
    flex: 1,
    padding: '0px 40px',
    paddingBottom: 20,
  },
  content: {
    overflowY: 'hidden',
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
    padding: '20px 40px',
    borderTop: theme.border,
    marginTop: 'auto',
  },
  selectButton: {
    width: 'auto',
  },
  closeIcon: {
    position: 'absolute',
    top: 30,
    right: 30,
    cursor: 'pointer',
    width: 20,
    height: 20,
  },
}))

/**
 * @typedef DatevClientService
 * @type {object}
 * @property {string} name
 * @property {string[]} scopes
 */

/**
 * @typedef DatevClient
 * @type {object}
 * @property {string} id
 * @property {number} client_number
 * @property {number} consultant_number
 * @property {string} name
 * @property {DatevClientService[]} services
 */

const DatevClientSelection = props => {
  /** @type {DatevClient[]} */
  const clients = props.clients || []
  const { currentClientId, onChange, className, close } = props
  const classes = useStyles()

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClient, setSelectedClient] = useState(clients.find(client => client.id === currentClientId))

  const clientOptions = useMemo(() => {
    return clients
      .filter((client) => isEmpty(searchTerm) || searchTerm.toLowerCase().split(' ').every(term => client.name.toLowerCase().includes(term) || client.client_number === parseInt(term, 10)))
      .map((client) => {
        const isSelected = selectedClient?.id === client.id

        return (
          <DatevClientSelectionItem
            key={client.id}
            client={client}
            isSelected={isSelected}
            onChange={setSelectedClient}
            consultantNumber={client.consultant_number}
            clientNumber={client.client_number}
          />
        )
      })
  }, [selectedClient, searchTerm])

  return (
    <div className={clsx(classes.container, className)}>
      <CloseIcon className={classes.closeIcon} onClick={close} />
      <div className={classes.content}>
        <Search customClass={classes.search} onSearchInputChange={setSearchTerm} placeholder={lang.accounting.datev.searchClientsPlaceholder} />
        <PerfectScrollbar className={classes.scrollArea}>
          {clientOptions}
        </PerfectScrollbar>
      </div>
      <div className={classes.footer}>
        <CancelButton type='cancel' label={lang.cancelButtonLabel} onClick={close} />
        <ButtonSquare
          className={classes.selectButton}
          label={lang.accounting.datev.selectButtonLabel}
          onClick={() => onChange(selectedClient)}
          disabled={!selectedClient}
        />
      </div>
    </div>
  )
}

DatevClientSelection.propTypes = {
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      client_number: PropTypes.number.isRequired,
      consultant_number: PropTypes.number.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          scopes: PropTypes.arrayOf(PropTypes.string),
        })),
    }),
  ),
  onChange: PropTypes.func,
  currentClientId: PropTypes.string,
  className: PropTypes.string,
}

export default DatevClientSelection
